import {
  appVersionSchema,
  contentVersionSchema,
  type LearnerJoinRequest,
  type LearnerJoinResponse,
  type LearnerProgressSnapshot,
  type LearnerRecoveryCredential,
  type LearningSessionResponse,
  type TaskAttemptRequest,
  type TaskAttemptResponse,
  taskAttemptRequestSchema,
  uuidSchema,
} from "@history-sim/api-contracts";
import type { LearningEvent } from "@history-sim/domain";
import { mapDomainEventForSync } from "./eventMapper";
import {
  LearnerApiHttpError,
  LearnerApiProtocolError,
  type LearnerSyncApi,
} from "./learnerApiClient";
import type {
  DeliveredTaskAttemptReceipt,
  IndexedDbLearningOutbox,
  QueuedClientLearningEvent,
  SyncSessionMapping,
} from "./learningOutbox";

const MAX_BATCHES_PER_FLUSH = 50;
const MAX_TASK_ATTEMPTS_PER_FLUSH = 50;

const RETRYABLE_TASK_ATTEMPT_PROTOCOL_CODES = new Set([
  "NETWORK_UNAVAILABLE",
  "REQUEST_TIMEOUT",
  "REQUEST_FAILED",
]);

function exactTaskAttemptResponse(
  serverSessionId: string,
  request: TaskAttemptRequest,
  response: TaskAttemptResponse,
): TaskAttemptResponse {
  if (
    response.sessionId !== serverSessionId ||
    response.clientAttemptId !== request.clientAttemptId ||
    response.contentVersion !== request.contentVersion ||
    response.taskId !== request.taskId
  ) {
    throw new LearnerApiProtocolError("TASK_ATTEMPT_ACKNOWLEDGEMENT_MISMATCH");
  }
  return response;
}

export class LearnerTaskAttemptQueuedError extends Error {
  readonly code = "TASK_ATTEMPT_QUEUED";
  readonly clientAttemptId: string;

  constructor(clientAttemptId: string) {
    super("Task attempt was queued for authoritative adjudication");
    this.name = "LearnerTaskAttemptQueuedError";
    this.clientAttemptId = clientAttemptId;
  }
}

export interface ConnectLearnerInput {
  readonly clientSessionId: string;
  readonly contentVersion: string;
  readonly appVersion: string;
  readonly identity: LearnerJoinRequest;
}

export interface RecoverLearnerInput {
  readonly clientSessionId: string;
  readonly contentVersion: string;
  readonly appVersion: string;
  readonly recoveryCode: string;
}

export type FlushResult =
  | {
      readonly status: "synced" | "idle";
      readonly sentEvents: number;
      readonly deadLetteredEvents: number;
      readonly pendingEvents: number;
    }
  | {
      readonly status: "auth-required" | "blocked" | "retry";
      readonly sentEvents: number;
      readonly deadLetteredEvents: number;
      readonly pendingEvents: number;
      readonly reasonCode: string;
      readonly retryAfterMs?: number;
    };

export interface ConnectLearnerResult {
  readonly profile: LearnerJoinResponse["profile"];
  readonly enrollment: LearnerJoinResponse["enrollment"];
  readonly learningSession: LearningSessionResponse;
  readonly recovery: LearnerRecoveryCredential;
  readonly progress: LearnerProgressSnapshot;
}

export type TaskAttemptFlushResult = {
  readonly status: "synced" | "idle" | "auth-required" | "blocked" | "retry";
  readonly delivered: readonly DeliveredTaskAttemptReceipt[];
  readonly deadLetteredAttempts: number;
  readonly pendingAttempts: number;
  readonly rejected: readonly {
    readonly clientAttemptId: string;
    readonly reasonCode: string;
  }[];
  readonly reasonCode?: string;
};

export class LearnerSyncCoordinator {
  readonly #api: LearnerSyncApi;
  readonly #outbox: IndexedDbLearningOutbox;
  #csrfToken: string | undefined;
  readonly #flushes = new Map<string, Promise<FlushResult>>();
  readonly #attemptFlushes = new Map<string, Promise<TaskAttemptFlushResult>>();

  constructor(api: LearnerSyncApi, outbox: IndexedDbLearningOutbox) {
    this.#api = api;
    this.#outbox = outbox;
  }

  async connect(input: ConnectLearnerInput): Promise<ConnectLearnerResult> {
    const clientSessionId = uuidSchema.parse(input.clientSessionId);
    const contentVersion = contentVersionSchema.parse(input.contentVersion);
    const appVersion = appVersionSchema.parse(input.appVersion);
    await this.#outbox.retireIncompatibleContentVersions(contentVersion);
    const existing = await this.#outbox.getSession(input.clientSessionId);
    if (existing && existing.appVersion !== appVersion) {
      throw new LearnerApiProtocolError("SYNC_SESSION_VERSION_MISMATCH");
    }
    if (existing) {
      try {
        this.#csrfToken = await this.#api.rotateCsrf();
        const rotated = await this.#api.rotateLearnerRecovery(this.#csrfToken);
        return {
          profile: {
            name: input.identity.name,
            major: input.identity.major,
            studentId: input.identity.studentId,
          },
          enrollment: {
            id: existing.enrollmentId,
            classroomId: existing.classroomId,
            classroomName: existing.classroomName,
            courseCode: existing.courseCode,
          },
          learningSession: this.#sessionResponse(existing),
          recovery: rotated.recovery,
          progress: rotated.progress,
        };
      } catch (error) {
        if (!(error instanceof LearnerApiHttpError) || ![401, 403].includes(error.status)) {
          throw error;
        }
      }
    }

    const joined = await this.#api.joinLearner(input.identity);
    this.#csrfToken = joined.csrfToken;
    const learningSession = await this.#api.createLearningSession(
      {
        clientSessionId,
        contentVersion,
        appVersion,
      },
      joined.csrfToken,
    );
    await this.#outbox.putSession({
      clientSessionId,
      serverSessionId: learningSession.sessionId,
      participantId: learningSession.participantId,
      enrollmentId: joined.enrollment.id,
      classroomId: joined.enrollment.classroomId,
      classroomName: joined.enrollment.classroomName,
      courseCode: joined.enrollment.courseCode,
      contentVersion: learningSession.contentVersion,
      appVersion: learningSession.appVersion,
      startedAt: learningSession.startedAt,
      updatedAt: learningSession.updatedAt,
    });
    return {
      profile: joined.profile,
      enrollment: joined.enrollment,
      learningSession,
      recovery: joined.recovery,
      progress: joined.progress,
    };
  }

  async recover(input: RecoverLearnerInput): Promise<ConnectLearnerResult> {
    const clientSessionId = uuidSchema.parse(input.clientSessionId);
    const contentVersion = contentVersionSchema.parse(input.contentVersion);
    const appVersion = appVersionSchema.parse(input.appVersion);
    await this.#outbox.retireIncompatibleContentVersions(contentVersion);
    const recovered = await this.#api.recoverLearner({ recoveryCode: input.recoveryCode });
    this.#csrfToken = recovered.csrfToken;
    const learningSession = await this.#api.createLearningSession(
      { clientSessionId, contentVersion, appVersion },
      recovered.csrfToken,
    );
    await this.#outbox.putSession({
      clientSessionId,
      serverSessionId: learningSession.sessionId,
      participantId: learningSession.participantId,
      enrollmentId: recovered.enrollment.id,
      classroomId: recovered.enrollment.classroomId,
      classroomName: recovered.enrollment.classroomName,
      courseCode: recovered.enrollment.courseCode,
      contentVersion: learningSession.contentVersion,
      appVersion: learningSession.appVersion,
      startedAt: learningSession.startedAt,
      updatedAt: learningSession.updatedAt,
    });
    return {
      profile: recovered.profile,
      enrollment: recovered.enrollment,
      learningSession,
      recovery: recovered.recovery,
      progress: recovered.progress,
    };
  }

  async resume(
    clientSessionId: string,
    contentVersion: string,
  ): Promise<SyncSessionMapping | undefined> {
    await this.#outbox.retireIncompatibleContentVersions(contentVersion);
    const mapping = await this.#outbox.getSession(clientSessionId);
    if (!mapping) return undefined;
    this.#csrfToken = await this.#api.rotateCsrf();
    return mapping;
  }

  async submitTaskAttempt(
    clientSessionId: string,
    input: TaskAttemptRequest,
  ): Promise<TaskAttemptResponse> {
    const sessionId = uuidSchema.parse(clientSessionId);
    const request = taskAttemptRequestSchema.parse(input);
    const mapping = await this.#outbox.getSession(sessionId);
    if (!mapping) throw new LearnerApiProtocolError("SYNC_SESSION_NOT_CONNECTED");
    if (mapping.contentVersion !== request.contentVersion) {
      throw new LearnerApiProtocolError("SYNC_SESSION_VERSION_MISMATCH");
    }

    try {
      if (!this.#csrfToken) this.#csrfToken = await this.#api.rotateCsrf();
      const response = await this.#api.submitTaskAttempt(
        mapping.serverSessionId,
        request,
        this.#csrfToken,
      );
      return exactTaskAttemptResponse(mapping.serverSessionId, request, response);
    } catch (error) {
      if (error instanceof LearnerApiHttpError && [401, 403].includes(error.status)) {
        this.#csrfToken = undefined;
      }
      if (
        error instanceof LearnerApiProtocolError &&
        RETRYABLE_TASK_ATTEMPT_PROTOCOL_CODES.has(error.code)
      ) {
        await this.#outbox.enqueueTaskAttempt(sessionId, request);
        throw new LearnerTaskAttemptQueuedError(request.clientAttemptId);
      }
      throw error;
    }
  }

  async flushTaskAttempts(clientSessionId: string): Promise<TaskAttemptFlushResult> {
    const existing = this.#attemptFlushes.get(clientSessionId);
    if (existing) return existing;
    const flush = this.#flushTaskAttemptsInternal(clientSessionId).finally(() => {
      this.#attemptFlushes.delete(clientSessionId);
    });
    this.#attemptFlushes.set(clientSessionId, flush);
    return flush;
  }

  async #flushTaskAttemptsInternal(clientSessionId: string): Promise<TaskAttemptFlushResult> {
    const sessionId = uuidSchema.parse(clientSessionId);
    const mapping = await this.#outbox.getSession(sessionId);
    const pendingAtStart = await this.#outbox.pendingTaskAttemptCount(sessionId);
    const delivered = () => this.#outbox.unappliedTaskAttemptReceipts(sessionId);
    if (!mapping) {
      return {
        status: pendingAtStart > 0 ? "auth-required" : "idle",
        delivered: await delivered(),
        deadLetteredAttempts: 0,
        pendingAttempts: pendingAtStart,
        rejected: [],
        ...(pendingAtStart > 0 ? { reasonCode: "SYNC_SESSION_NOT_CONNECTED" } : {}),
      };
    }
    if (pendingAtStart === 0) {
      return {
        status: "idle",
        delivered: await delivered(),
        deadLetteredAttempts: 0,
        pendingAttempts: 0,
        rejected: [],
      };
    }

    let deadLetteredAttempts = 0;
    const rejected: { clientAttemptId: string; reasonCode: string }[] = [];
    for (let index = 0; index < MAX_TASK_ATTEMPTS_PER_FLUSH; index += 1) {
      const claimed = await this.#outbox.claimTaskAttempt(sessionId);
      if (!claimed) {
        return {
          status: "synced",
          delivered: await delivered(),
          deadLetteredAttempts,
          pendingAttempts: 0,
          rejected,
        };
      }
      try {
        if (!this.#csrfToken) this.#csrfToken = await this.#api.rotateCsrf();
        const response = exactTaskAttemptResponse(
          mapping.serverSessionId,
          claimed.request,
          await this.#api.submitTaskAttempt(
            mapping.serverSessionId,
            claimed.request,
            this.#csrfToken,
          ),
        );
        await this.#outbox.acknowledgeTaskAttempt(claimed, response);
      } catch (error) {
        if (error instanceof LearnerApiHttpError && [400, 409, 422].includes(error.status)) {
          if (await this.#outbox.deadLetterTaskAttempt(claimed, error.code)) {
            deadLetteredAttempts += 1;
            rejected.push({
              clientAttemptId: claimed.request.clientAttemptId,
              reasonCode: error.code,
            });
          }
          if (error.status === 409) {
            return {
              status: "blocked",
              delivered: await delivered(),
              deadLetteredAttempts,
              pendingAttempts: await this.#outbox.pendingTaskAttemptCount(sessionId),
              rejected,
              reasonCode: error.code,
            };
          }
          continue;
        }
        if (error instanceof LearnerApiHttpError && [401, 403].includes(error.status)) {
          this.#csrfToken = undefined;
          return {
            status: "auth-required",
            delivered: await delivered(),
            deadLetteredAttempts,
            pendingAttempts: await this.#outbox.pendingTaskAttemptCount(sessionId),
            rejected,
            reasonCode: error.code,
          };
        }
        return {
          status: "retry",
          delivered: await delivered(),
          deadLetteredAttempts,
          pendingAttempts: await this.#outbox.pendingTaskAttemptCount(sessionId),
          rejected,
          reasonCode:
            error instanceof LearnerApiProtocolError ? error.code : "TASK_ATTEMPT_SYNC_FAILED",
        };
      }
    }

    return {
      status: "retry",
      delivered: await delivered(),
      deadLetteredAttempts,
      pendingAttempts: await this.#outbox.pendingTaskAttemptCount(sessionId),
      rejected,
      reasonCode: "TASK_ATTEMPT_FLUSH_LIMIT_REACHED",
    };
  }

  async enqueue(
    clientSessionId: string,
    events: readonly LearningEvent[],
  ): Promise<{
    readonly enqueued: number;
    readonly serverAuthoritative: number;
    readonly invalid: number;
  }> {
    const clientEvents: QueuedClientLearningEvent[] = [];
    let serverAuthoritative = 0;
    let invalid = 0;
    for (const event of events) {
      const mapped = mapDomainEventForSync(event);
      if (mapped.kind === "enqueue") {
        clientEvents.push({
          event: mapped.event,
          contentVersion: event.contentVersion,
          appVersion: event.appVersion,
        });
      } else if (mapped.kind === "server-authoritative") serverAuthoritative += 1;
      else invalid += 1;
    }
    const enqueued = await this.#outbox.enqueue(clientSessionId, clientEvents);
    return { enqueued, serverAuthoritative, invalid };
  }

  async flush(clientSessionId: string): Promise<FlushResult> {
    const existing = this.#flushes.get(clientSessionId);
    if (existing) return existing;
    const flush = this.#flushInternal(clientSessionId).finally(() => {
      this.#flushes.delete(clientSessionId);
    });
    this.#flushes.set(clientSessionId, flush);
    return flush;
  }

  async #flushInternal(clientSessionId: string): Promise<FlushResult> {
    const mapping = await this.#outbox.getSession(clientSessionId);
    const pendingAtStart = await this.#outbox.pendingCount(clientSessionId);
    if (pendingAtStart === 0) {
      return {
        status: "idle",
        sentEvents: 0,
        deadLetteredEvents: 0,
        pendingEvents: 0,
      };
    }
    if (!mapping) {
      return {
        status: "auth-required",
        sentEvents: 0,
        deadLetteredEvents: 0,
        pendingEvents: pendingAtStart,
        reasonCode: "SYNC_SESSION_NOT_CONNECTED",
      };
    }
    if (!this.#csrfToken) {
      try {
        this.#csrfToken = await this.#api.rotateCsrf();
      } catch (error) {
        const status =
          error instanceof LearnerApiHttpError && [401, 403].includes(error.status)
            ? "auth-required"
            : "retry";
        return this.#failure(status, error, 0, 0, pendingAtStart);
      }
    }

    let sentEvents = 0;
    let deadLetteredEvents = 0;
    for (let batchIndex = 0; batchIndex < MAX_BATCHES_PER_FLUSH; batchIndex += 1) {
      const batch = await this.#outbox.claimBatch(clientSessionId);
      if (!batch) {
        return {
          status: sentEvents === 0 && deadLetteredEvents === 0 ? "idle" : "synced",
          sentEvents,
          deadLetteredEvents,
          pendingEvents: 0,
        };
      }
      try {
        const response = await this.#api.appendEventBatch(
          mapping.serverSessionId,
          batch.request,
          this.#csrfToken,
        );
        await this.#outbox.acknowledgeBatch(batch, response);
        sentEvents += batch.request.events.length;
      } catch (error) {
        if (error instanceof LearnerApiHttpError && [400, 422].includes(error.status)) {
          deadLetteredEvents += await this.#outbox.deadLetterBatch(batch, error.code);
          continue;
        }
        if (error instanceof LearnerApiHttpError && [401, 403].includes(error.status)) {
          this.#csrfToken = undefined;
          return this.#failure(
            "auth-required",
            error,
            sentEvents,
            deadLetteredEvents,
            await this.#outbox.pendingCount(clientSessionId),
          );
        }
        const status =
          error instanceof LearnerApiHttpError && error.status === 409 ? "blocked" : "retry";
        return this.#failure(
          status,
          error,
          sentEvents,
          deadLetteredEvents,
          await this.#outbox.pendingCount(clientSessionId),
        );
      }
    }

    return {
      status: "retry",
      sentEvents,
      deadLetteredEvents,
      pendingEvents: await this.#outbox.pendingCount(clientSessionId),
      reasonCode: "SYNC_FLUSH_LIMIT_REACHED",
    };
  }

  #failure(
    status: "auth-required" | "blocked" | "retry",
    error: unknown,
    sentEvents: number,
    deadLetteredEvents: number,
    pendingEvents: number,
  ): FlushResult {
    if (error instanceof LearnerApiHttpError) {
      return {
        status,
        sentEvents,
        deadLetteredEvents,
        pendingEvents,
        reasonCode: error.code,
        ...(error.retryAfterMs !== undefined ? { retryAfterMs: error.retryAfterMs } : {}),
      };
    }
    return {
      status,
      sentEvents,
      deadLetteredEvents,
      pendingEvents,
      reasonCode: error instanceof LearnerApiProtocolError ? error.code : "SYNC_UNEXPECTED_FAILURE",
    };
  }

  #sessionResponse(mapping: SyncSessionMapping): LearningSessionResponse {
    return {
      sessionId: mapping.serverSessionId,
      clientSessionId: mapping.clientSessionId,
      participantId: mapping.participantId,
      contentVersion: mapping.contentVersion,
      appVersion: mapping.appVersion,
      status: "active",
      startedAt: mapping.startedAt,
      updatedAt: mapping.updatedAt,
    };
  }
}
